import { createContext, useContext, useState, useCallback } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { CheckCircle2, XCircle, Info, AlertTriangle } from 'lucide-react'

// ─── Types ───────────────────────────────────────────────────────────────────

type ToastVariant = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: string
  message: string
  variant: ToastVariant
}

interface ToastContextValue {
  toast: {
    success: (msg: string) => void
    error: (msg: string) => void
    info: (msg: string) => void
    warning: (msg: string) => void
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue>({
  toast: {
    success: () => undefined,
    error: () => undefined,
    info: () => undefined,
    warning: () => undefined,
  },
})

export function useToast(): ToastContextValue {
  return useContext(ToastContext)
}

// ─── Variant config ──────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<
  ToastVariant,
  { bg: string; border: string; Icon: React.ComponentType<{ size?: number; color?: string }> }
> = {
  success: {
    bg: 'color-mix(in srgb, var(--success) 12%, var(--surface))',
    border: '3px solid var(--success)',
    Icon: CheckCircle2,
  },
  error: {
    bg: 'color-mix(in srgb, var(--danger) 12%, var(--surface))',
    border: '3px solid var(--danger)',
    Icon: XCircle,
  },
  info: {
    bg: 'color-mix(in srgb, var(--brand) 12%, var(--surface))',
    border: '3px solid var(--brand)',
    Icon: Info,
  },
  warning: {
    bg: 'color-mix(in srgb, var(--accent) 12%, var(--surface))',
    border: '3px solid var(--accent)',
    Icon: AlertTriangle,
  },
}

const VARIANT_ICON_COLOR: Record<ToastVariant, string> = {
  success: 'var(--success)',
  error: 'var(--danger)',
  info: 'var(--brand)',
  warning: 'var(--accent)',
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback((message: string, variant: ToastVariant) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    setToasts((prev) => {
      const next = [...prev, { id, message, variant }]
      // Max 3 toasts — drop oldest
      return next.length > 3 ? next.slice(next.length - 3) : next
    })
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3000)
  }, [])

  const toast = {
    success: (msg: string) => addToast(msg, 'success'),
    error: (msg: string) => addToast(msg, 'error'),
    info: (msg: string) => addToast(msg, 'info'),
    warning: (msg: string) => addToast(msg, 'warning'),
  }

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 16,
          insetInlineStart: '50%',
          transform: 'translateX(-50%)',
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: 'min(360px, calc(100vw - 32px))',
          pointerEvents: 'none',
        }}
      >
        <AnimatePresence mode="popLayout">
          {toasts.map((t) => {
            const cfg = VARIANT_CONFIG[t.variant]
            const { Icon } = cfg
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
                style={{
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-modal)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: cfg.bg,
                  borderInlineStart: cfg.border,
                  pointerEvents: 'auto',
                }}
              >
                <Icon size={18} color={VARIANT_ICON_COLOR[t.variant]} />
                <span
                  style={{
                    fontSize: 'var(--fs-sm)',
                    fontFamily: 'var(--font-sans)',
                    color: 'var(--text-strong)',
                    flex: 1,
                  }}
                >
                  {t.message}
                </span>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}
